import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Lock as LockIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';

interface FeatureSettingsData {
  features: Record<string, boolean>;
  governance: any;
  restrictions: string[];
}

interface FeatureSettingsProps {
  salonId: string;
}

const FeatureSettings: React.FC<FeatureSettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<FeatureSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('features', 'write');
  const accessRestrictions = getRestrictions('features');

  // Feature categories for better organization
  const featureCategories = {
    'Core Business Features': [
      'memberships',
      'loyaltyPoints',
      'homeServices',
      'onlineBooking',
      'walkInBooking',
      'staffScheduling',
      'inventoryManagement',
      'financialReporting'
    ],
    'Premium Services': [
      'premiumServices',
      'luxuryPackages',
      'vipTreatment',
      'brandedProducts',
      'makeupCategories'
    ],
    'Marketing & Communication': [
      'marketingCampaigns',
      'smsMarketing',
      'emailCampaigns',
      'socialMediaIntegration',
      'reviewManagement',
      'referralPrograms'
    ],
    'Advanced Features': [
      'customerAnalytics',
      'aiRecommendations',
      'predictiveAnalytics',
      'customIntegrations',
      'apiAccess',
      'webhooks'
    ],
    'Discount & Pricing': [
      'discountEngine'
    ]
  };

  const featureDescriptions: Record<string, string> = {
    memberships: 'Enable membership programs for customers',
    loyaltyPoints: 'Reward customers with loyalty points',
    homeServices: 'Offer services at customer locations',
    onlineBooking: 'Allow customers to book appointments online',
    walkInBooking: 'Accept walk-in appointments',
    discountEngine: 'Create and manage discount campaigns',
    makeupCategories: 'Organize makeup services by categories',
    marketingCampaigns: 'Create and manage marketing campaigns',
    premiumServices: 'Offer premium service packages',
    luxuryPackages: 'High-end luxury service packages',
    vipTreatment: 'Special VIP customer treatment',
    brandedProducts: 'Sell branded beauty products',
    staffScheduling: 'Advanced staff scheduling system',
    inventoryManagement: 'Track and manage inventory',
    financialReporting: 'Generate financial reports',
    customerAnalytics: 'Analyze customer behavior and preferences',
    smsMarketing: 'Send SMS marketing campaigns',
    emailCampaigns: 'Create email marketing campaigns',
    socialMediaIntegration: 'Connect with social media platforms',
    reviewManagement: 'Manage customer reviews and ratings',
    referralPrograms: 'Customer referral reward programs',
    aiRecommendations: 'AI-powered service recommendations',
    predictiveAnalytics: 'Predict customer behavior and trends',
    customIntegrations: 'Connect with third-party services',
    apiAccess: 'Access to API for custom development',
    webhooks: 'Real-time event notifications'
  };

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/features`);
      
      if (response.success) {
        setSettings(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load feature settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canWrite || !settings) {
      setError('You do not have permission to modify these settings');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const response = await apiClient.put(`/settings/${salonId}/features`, settings.features);
      
      if (response.success) {
        setSuccess('Feature settings updated successfully');
        setTimeout(() => setSuccess(null), 3000);
        // Refresh to get updated governance info
        await fetchSettings();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update feature settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (featureName: string, enabled: boolean) => {
    if (!settings || !canWrite) return;

    // Check if feature is restricted
    const isRestricted = isFeatureRestricted(featureName);
    if (isRestricted && enabled) {
      setError(`Cannot enable ${featureName}: restricted by governance rules`);
      return;
    }

    setSettings({
      ...settings,
      features: {
        ...settings.features,
        [featureName]: enabled
      }
    });
  };

  const isFeatureRestricted = (featureName: string): boolean => {
    if (!settings) return false;

    const restrictedFeatures = [
      'premiumServices',
      'luxuryPackages',
      'vipTreatment',
      'brandedProducts',
      'socialMediaIntegration',
      'reviewManagement',
      'referralPrograms'
    ];

    return settings.governance?.salonType === 'SILENT_UTILITY' && 
           restrictedFeatures.includes(featureName);
  };

  const getFeatureStatus = (featureName: string) => {
    if (!settings) return { enabled: false, restricted: false };
    
    const enabled = settings.features[featureName] || false;
    const restricted = isFeatureRestricted(featureName);
    
    return { enabled, restricted };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">
        Failed to load feature settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Feature Configuration</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {accessRestrictions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {accessRestrictions.map(restriction => (
                <Chip
                  key={restriction}
                  icon={<LockIcon />}
                  label={restriction.replace(/_/g, ' ')}
                  color="warning"
                  size="small"
                />
              ))}
            </Box>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Governance Information */}
      {settings.governance && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<InfoIcon />}
        >
          <Typography variant="subtitle2" gutterBottom>
            Salon Type: {settings.governance.salonType?.replace('_', ' ')}
          </Typography>
          <Typography variant="body2">
            Brand Protection Level: {settings.governance.brandProtectionLevel}
            {settings.governance.positioningLocked && ' • Positioning Locked'}
          </Typography>
          {settings.restrictions.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Active Restrictions: {settings.restrictions.join(', ').replace(/_/g, ' ')}
            </Typography>
          )}
        </Alert>
      )}

      {/* Feature Categories */}
      <Grid container spacing={2}>
        {Object.entries(featureCategories).map(([categoryName, featureList]) => (
          <Grid item xs={12} key={categoryName}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{categoryName}</Typography>
                <Chip 
                  label={`${featureList.filter(f => settings.features[f]).length}/${featureList.length} enabled`}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {featureList.map(featureName => {
                    const { enabled, restricted } = getFeatureStatus(featureName);
                    const description = featureDescriptions[featureName] || 'Feature description not available';
                    
                    return (
                      <ListItem key={featureName} divider>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {featureName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                              </Typography>
                              {restricted && (
                                <Tooltip title="This feature is restricted by governance rules">
                                  <Chip
                                    icon={<WarningIcon />}
                                    label="Restricted"
                                    color="error"
                                    size="small"
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          }
                          secondary={description}
                        />
                        <ListItemSecondaryAction>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={enabled}
                                onChange={(e) => toggleFeature(featureName, e.target.checked)}
                                disabled={!canWrite || (restricted && !enabled)}
                              />
                            }
                            label=""
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      {canWrite && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FeatureSettings;